import React, {useEffect, useState} from "react";
import {Link, NavigateFunction, useNavigate, useParams} from "react-router-dom";
import ITicket from "../../types/ticket.type";
import { getAllTickets} from "../../services/api.service";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    ColumnDef,
    SortingState,
} from '@tanstack/react-table';

const Ticket: React.FC = () => {
    const navigate: NavigateFunction = useNavigate();
    const [loading, setLoading] = useState(false);
    const {status} = useParams();
    const {label} = useParams();
    const [tickets, setTickets] = useState<ITicket[]>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([]);

    useEffect(() => {
        const loadTickets = async () => {
            setLoading(true);

            const ticket_status: string = status || "";
            const ticket_label: string = label || "";

            try {
                const data = await getAllTickets(ticket_status, ticket_label);

                setTickets(data);
            } catch (error) {
                console.error('Failed to fetch tickets', error);
            } finally {
                setLoading(false);
            }
        };

        loadTickets();
    }, [status, label]);

      // Define column configuration
    const columns: ColumnDef<ITicket>[] = [
        {
            header: () => 'Title',
            accessorKey: 'title',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Description',
            accessorKey: 'description',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Station',
            accessorKey: 'station',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            header: () => 'Created At',
            accessorKey: 'createdAt',
            cell: info => info.getValue(),
            filterFn: 'includesString',
        },
        {
            id: 'details',
            header: '',
            cell: ({row}) => (
                <a href={`https://tickets.tahmo.org/#/issues/view/${row.original.Id}`} className="btn btn-sm btn-outline-primary" target="_blank" rel="noopener noreferrer">
                    <i className="mdi mdi-eye"></i>
                </a>
            ),
        }
    ];

     const table = useReactTable<ITicket>({
        data: tickets,
        columns,
        state: {
            globalFilter,
            sorting,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: 'includesString',
    });

 return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box">
                                <h4 className="page-title">Tickets</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h4 className="header-title">Tickets</h4>
                                    <input
                                        className="form-control form-control-sm"
                                        style={{width: '250px'}}
                                        placeholder="Search..."
                                        value={globalFilter ?? ''}
                                        onChange={e => setGlobalFilter(e.target.value)}
                                    />
                                </div>
                                <div className="card-body pt-0">
                                    {loading ? (
                                        <p>Loading...</p>
                                    ) : (
                                        <table
                                            className="table dt-responsive nowrap w-100"
                                        >
                                            <thead>
                                            {table.getHeaderGroups().map(headerGroup => (
                                                <tr key={headerGroup.id}>
                                                    {headerGroup.headers.map(header => (
                                                        <th
                                                            key={header.id}
                                                            onClick={header.column.getToggleSortingHandler()}
                                                            style={{cursor: 'pointer'}}
                                                        >
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                            {header.column.getIsSorted() === 'asc' && ' 🔼'}
                                                            {header.column.getIsSorted() === 'desc' && ' 🔽'}
                                                        </th>
                                                    ))}
                                                </tr>
                                            ))}
                                            </thead>
                                            <tbody>
                                            {table.getRowModel().rows.map(row => (
                                                <tr key={row.id}>
                                                    {row.getVisibleCells().map(cell => (
                                                        <td key={cell.id}>
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                            {table.getRowModel().rows.length === 0 && (
                                                <tr>
                                                    <td colSpan={columns.length} className="text-center text-muted">
                                                        No results found.
                                                    </td>
                                                </tr>
                                            )}

                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="card-footer d-flex justify-content-between align-items-center">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-primary me-2"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            Previous
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <span>
                      Page {table.getState().pagination.pageIndex + 1} of{' '}
                                        {table.getPageCount()}
                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );


}
export default Ticket;